import {
  Box,
  Button,
  Card,
  CardBody,
  Checkbox,
  Flex,
  Heading,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import permissionService from "./permissionService";
import OptionsService from "../Os/OptionsOsService";
import { normalizeAccessRole } from "../User/accessRoleUtils";
import ModernPageShell from "../../components/layout/ModernPageShell";
import { getModernUi } from "../../components/layout/modernUi";

const FEATURES = [
  { key: "HOME_OS", label: "Home / OS" },
  { key: "CRIACAO_OS", label: "Criação de OS" },
  { key: "DENUNCIAS", label: "Denúncias" },
  { key: "USUARIOS", label: "Usuários" },
  { key: "EMPRESAS", label: "Empresas" },
  { key: "CADASTRO_OPCOES", label: "Cadastro de opções" },
  { key: "PERMISSOES", label: "Permissões" },
];

export default function Permissions({ modern = false }) {
  const ui = getModernUi(modern);
  const toast = useToast();
  const [roles, setRoles] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [profileOptions, items] = await Promise.all([
        OptionsService.getOptionsByType("role"),
        permissionService.getAll(),
      ]);

      const roleNames = Array.from(
        new Set(
          (profileOptions || [])
            .map((option) => normalizeAccessRole(option.description))
            .filter(Boolean)
        )
      ).sort();

      setRoles(roleNames);

      const next = {};
      roleNames.forEach((role) => {
        next[role] = {};
        FEATURES.forEach((feature) => {
          next[role][feature.key] = false;
        });
      });
      (items || []).forEach((item) => {
        if (next[item.role] && item.feature in next[item.role]) {
          next[item.role][item.feature] = !!item.allowed;
        }
      });
      setMatrix(next);
    } catch (error) {
      toast({
        title: "Erro ao carregar permissões",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (role, feature) => {
    setMatrix((prev) => ({
      ...prev,
      [role]: {
        ...prev[role],
        [feature]: !prev[role][feature],
      },
    }));
  };

  const payload = useMemo(() => {
    const items = [];
    roles.forEach((role) => {
      FEATURES.forEach((feature) => {
        items.push({
          role,
          feature: feature.key,
          allowed: !!matrix?.[role]?.[feature.key],
        });
      });
    });
    return items;
  }, [matrix, roles]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await permissionService.saveAll(payload);
      toast({
        title: "Permissões salvas",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      await load();
    } catch (error) {
      toast({
        title: "Erro ao salvar",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const pageContent = (
    <>
      <Flex justify="space-between" align="center" mb={4} gap={4} wrap="wrap">
        <Box>
          {!modern && <Heading size="lg">Permissões por perfil</Heading>}
          <Text color="gray.600" mt={modern ? 0 : 1}>
            Define quais telas/menus cada perfil pode usar. Consulta de OS (CDHU,
            PRODESP...) é controlada separadamente pelo perfil + empresa
            vinculada do usuário.
          </Text>
        </Box>
        <Button colorScheme="red" onClick={handleSave} isLoading={isSaving}>
          Salvar
        </Button>
      </Flex>

      <Card {...ui.panel}>
        <CardBody>
          {isLoading ? (
            <Flex justify="center" py={10}>
              <Spinner />
            </Flex>
          ) : roles.length === 0 ? (
            <Text color="gray.600">
              Nenhum perfil cadastrado. Crie em Cadastro de opções → Perfil
              (acesso).
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Funcionalidade</Th>
                    {roles.map((role) => (
                      <Th key={role} textAlign="center">
                        {role}
                      </Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {FEATURES.map((feature) => (
                    <Tr key={feature.key}>
                      <Td fontWeight="medium">{feature.label}</Td>
                      {roles.map((role) => (
                        <Td key={`${role}-${feature.key}`} textAlign="center">
                          <Checkbox
                            isChecked={!!matrix?.[role]?.[feature.key]}
                            onChange={() => toggle(role, feature.key)}
                          />
                        </Td>
                      ))}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>
    </>
  );

  if (modern) {
    return (
      <ModernPageShell
        title="Permissões"
        subtitle="Configure o que cada perfil de acesso pode visualizar e executar."
        eyebrow="Segurança"
      >
        {pageContent}
      </ModernPageShell>
    );
  }

  return <Box p={6}>{pageContent}</Box>;
}
